import React from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { BeatLoader } from 'react-spinners'
import { FaEdit, FaTrashAlt } from 'react-icons/fa'

// dayjs plugins
dayjs.extend(customParseFormat)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

function formatSeatSize(size) {
  if (!size) return 'N/A'
  return Array.isArray(size) ? size.join(', ') : size
}

function VariantsTable({
  saddles = [],
  loading,
  sortConfig,
  handleSort,
  onEdit,
  onDelete,
  currentlyDeleting,
  deleteSaddleLoading,
}) {
  const variantSaddles = React.useMemo(() => {
    return saddles?.filter(
      saddle =>
        saddle.type === 'VARIANT' ||
        (saddle?.sk && String(saddle.sk).includes('#variant#')),
    )
  }, [saddles])

  const [deletingItems, setDeletingItems] = React.useState({})

  const columns = [
    { key: 'size', label: 'Seat Size' },
    { key: 'price', label: 'Price' },
    { key: 'quantity', label: 'Quantity' },
  ]

  const numericColumns = ['price', 'quantity', 'size']

  const handleDelete = async saddle => {
    setDeletingItems(prev => ({ ...prev, [saddle.sk]: true }))
    try {
      await onDelete(saddle)
    } finally {
      setDeletingItems(prev => ({ ...prev, [saddle.sk]: false }))
    }
  }

  const sortedSaddles = React.useMemo(() => {
    if (sortConfig.key && variantSaddles.length) {
      return [...variantSaddles].sort((a, b) => {
        let aValue = a[sortConfig.key] ?? ''
        let bValue = b[sortConfig.key] ?? ''

        if (Array.isArray(aValue)) aValue = aValue.join(', ')
        if (Array.isArray(bValue)) bValue = bValue.join(', ')

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        }

        aValue = Number(aValue) || 0
        bValue = Number(bValue) || 0

        return sortConfig.direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue
      })
    }
    return variantSaddles
  }, [variantSaddles, sortConfig])

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <BeatLoader color="#2B364B" />
      </div>
    )
  }

  if (!sortedSaddles.length) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-3xl text-gray-500">
          No Saddle Variants Found.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto mb-10">
      <table
        className="min-w-full overflow-hidden"
        style={{
          border: '1px solid #999',
          borderRadius: '10px',
          borderSpacing: '0',
        }}
      >
        <thead>
          <tr className="table-head">
            {columns.map(({ key, label }) => (
              <th
                key={key}
                scope="col"
                align={numericColumns.includes(key) ? 'center' : 'left'}
                className="px-3 py-2 group"
                style={{
                  borderBottom: '1px solid #999',
                  cursor: 'pointer',
                }}
                onClick={() => handleSort(key)}
              >
                {label}
                <span className="text-sm ml-1">
                  {sortConfig.key === key ? (
                    sortConfig.direction === 'ascending' ? (
                      '↑'
                    ) : (
                      '↓'
                    )
                  ) : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
                </span>
              </th>
            ))}
            <th
              scope="col"
              className="px-3 py-2"
              style={{ borderBottom: '1px solid #999' }}
            >
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {sortedSaddles.map((saddle, index) => (
            <tr key={index}>
              <td
                align="center"
                className="px-3 py-4 whitespace-nowrap font-montserrat font-medium"
                style={{ borderBottom: '1px solid #999' }}
              >
                {formatSeatSize(saddle.size)}
              </td>

              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap font-montserrat font-[16px]"
                style={{ borderBottom: '1px solid #999' }}
              >
                {saddle.price
                  ? `$${Number(saddle.price).toLocaleString()}`
                  : 'N/A'}
              </td>

              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap font-montserrat font-[16px]"
                style={{ borderBottom: '1px solid #999' }}
              >
                {saddle.quantity ?? 'N/A'}
              </td>

              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap font-montserrat font-[16px]"
                style={{
                  borderBottom: '1px solid #999',
                  position: 'relative',
                }}
              >
                <div className="flex items-center justify-center gap-4">
                  <FaEdit
                    className={`cursor-pointer text-[#2B364B]`}
                    onClick={() => onEdit(saddle)}
                  />

                  {currentlyDeleting === saddle.sk && deleteSaddleLoading ? (
                    <BeatLoader color="#2B364B" size={8} />
                  ) : (
                    <FaTrashAlt
                      className={`${'cursor-pointer text-red-500'}`}
                      onClick={() => handleDelete(saddle)}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default VariantsTable
